'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getCertificateListService } from '@/services/httpServices/CertificateServices';
import type { CertificateListResponse, CertificateListServiceConfig } from '@/services/httpServices/CertificateServices/types';

import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { certificateListKeys, infiniteCertificateListKeys } from './useCertificateQueryKeys';
import { useQueryConfig } from './useQueryConfig';

export function useCertificateListQuery(params: CertificateListServiceConfig, enabled: boolean = true) {
  const { isEnabled } = useQueryConfig();

  return useQuery({
    queryKey: certificateListKeys(params),
    queryFn: async () => getCertificateListService(params),
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch certificate list with infinite scrolling using cursor-based
 * pagination. Used by the kanban view so the board fetches pages as the user
 * scrolls each column to the bottom.
 */
export function useInfiniteCertificateListQuery(
  initialParams: CertificateListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<CertificateListResponse>({
    queryKey: infiniteCertificateListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getCertificateListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled,
  });
}
